/**
 * Coach Engine — Setup Scope
 *
 * Per-setup observation orchestrator. Filters trades to the named setup,
 * runs registered setup-scope analyzers, returns ranked observations.
 *
 * Different from day/progress scopes:
 *   - day:      one date, all setups
 *   - progress: all setups, last N days
 *   - setup:    one setup, all of its tracked history
 *
 * Adding a new setup-scope analyzer:
 *   1. Create the module under ../analyzers/setup/
 *   2. Push into ANALYZERS below
 *   3. Done — ranking is handled here, scope dispatch wires up in index.js.
 */

import { topObservations } from '../priorities';
import { synthesizeAndPrepend } from '../synthesis/synthesize';
import { enrichWithTemporalContext } from '../synthesis/temporalContext';
import { getRuntimeContext } from '../profile/runtimeContext';
import { buildEmptyScopeHint } from '../emptyScopeHint';
import * as setupBarContext           from '../analyzers/setup/setupBarContext';
import * as setupConfluenceFit        from '../analyzers/setup/setupConfluenceFit';
import * as setupAtmFit               from '../analyzers/setup/setupAtmFit';
import * as emaProximityIntelligence  from '../analyzers/composite/emaProximityIntelligence';
import * as confluenceComposite       from '../analyzers/composite/confluenceComposite';
import * as mistakeClustering         from '../analyzers/composite/mistakeClustering';
import * as setupRuleValidation       from '../analyzers/composite/setupRuleValidation';
import * as convictionCalibration     from '../analyzers/composite/convictionCalibration';
import * as inTradeCounterSignal      from '../analyzers/composite/inTradeCounterSignal';

const ANALYZERS = [
  setupBarContext,
  setupConfluenceFit,
  setupAtmFit,
  // Composite analyzers — read multiple barAnalysis + confluence fields
  // together to surface signals the per-domain analyzers can't see in
  // isolation. Pullback level identification, chase penalty, winning
  // confluence pairs, counter-confluence overrides, reserved confluences,
  // mistake hotspot clustering, recommended-confluence rule compliance,
  // conviction-tier calibration (Phase 15).
  emaProximityIntelligence,
  confluenceComposite,
  mistakeClustering,
  setupRuleValidation,
  convictionCalibration,
  // Phase 19 — Clusters trades by which counter-signals fired between
  // entry and exit, then correlates with outcomes. Surfaces patterns
  // like "15m close vs 9 EMA correctly predicts 70% of your losers."
  // Reads trade.barAnalysis.counterSignals populated at backfill.
  inTradeCounterSignal,
];

const TIER_LEVEL = { free: 0, pro: 1 };

const analyzersForTier = (userTier = 'free') => {
  const level = TIER_LEVEL[userTier] ?? 0;
  return ANALYZERS.filter(a => (TIER_LEVEL[a.tier || 'free'] ?? 0) <= level);
};

/**
 * Run the registered setup-scope analyzers and return ranked observations.
 *
 * @param {Object} ctx
 * @param {string} ctx.setupName            The setup being analyzed (matched against trade.setup)
 * @param {Array}  ctx.allTrades            Full trades array — analyzer filters to this setup
 * @param {string} [ctx.anchorDate]         Anchor for "recent vs prior" comparisons (today's trading day)
 * @param {string} [ctx.tier]               'free' | 'pro' — tier-gates analyzers
 * @param {number} [ctx.top]                Cap output count (default unlimited)
 * @param {Array}  [ctx.allConfluences]     Confluence library — for id/name resolution + recommendation actions
 * @param {Array}  [ctx.allMistakes]        Mistakes library — for cross-tab mistake clustering by setup
 * @param {Object} [ctx.atmDetails]         ATM template configurations (bracket/trail/target by name)
 * @param {Object} [ctx.setupDetails]       This setup's existing description/rules/recommendedConfluences
 * @param {Object} [ctx.allJournalEntries]  Journal entries keyed by date — for news/event sensitivity
 * @param {Function} [ctx.getTradeNetPnL]   (trade, account) → { netPnl } for P&L attribution
 * @param {Function} [ctx.getAccountById]   (id) → account
 * @returns {Array<Observation>}
 */
export const analyzeSetup = (ctx = {}) => {
  const {
    setupName, allTrades = [], anchorDate, tier = 'free', top,
    allConfluences = [], allMistakes = [],
    atmDetails = {}, setupDetails = {},
    allJournalEntries = {},
    getTradeNetPnL, getAccountById,
  } = ctx;
  if (!setupName) return [];
  if (!Array.isArray(allTrades) || allTrades.length === 0) return [];

  // Filter to this setup. Trades without barAnalysis are still included —
  // individual analyzers can decide how to handle them (most skip).
  const setupTrades = allTrades.filter(t => t && t.setup === setupName);
  if (setupTrades.length === 0) return [];

  // Enrich with commission-aware netPnl. Setup-scope analyzers
  // (setupAtmFit / setupConfluenceFit / setupBarContext) all read
  // `t.netPnl > 0` to count winners, but raw trade objects don't carry a
  // netPnl field — that's computed on demand from `trade.pnl` minus
  // commissions via the getTradeNetPnL callback. Without this enrichment
  // every trade looks like a non-winner (Number.isFinite(undefined) is
  // false), so every WR comes back as 0% and no observation hits its
  // threshold. Mirrors the same enrichment dayUtils.buildDayContext does
  // for the day scope.
  const netPnlFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
  };
  const enrichedSetupTrades = setupTrades.map(t => ({ ...t, netPnl: netPnlFor(t) }));

  const analyzers = analyzersForTier(tier);
  const all = [];
  for (const analyzer of analyzers) {
    try {
      const result = analyzer.analyze({
        setupName,
        setupTrades: enrichedSetupTrades,
        // Unfiltered trades — for analyzers that need a CROSS-SETUP baseline
        // (e.g., "is this setup's mistake rate above the trader's average?").
        // Most analyzers ignore this; mistakeClustering reads it.
        allTrades,
        anchorDate,
        tier,
        // Cross-tab context — pass everything analyzers might want so we
        // don't have to re-thread per analyzer. Each analyzer reads only
        // what it needs.
        allConfluences,
        allMistakes,
        atmDetails,
        setupDetails,
        allJournalEntries,
        getTradeNetPnL,
        getAccountById,
        // The setup's currently-recommended confluences — useful so an
        // analyzer can avoid recommending what's already pinned.
        currentRecommendedConfluences: setupDetails[setupName]?.recommendedConfluences || [],
      });
      if (Array.isArray(result)) all.push(...result);
      else if (result) all.push(result);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Coach setup] ${analyzer.key || 'unknown'} failed:`, e?.message || e);
    }
  }

  if (all.length === 0) {
    // Analyzers all returned null — surface a "needs more data" hint
    // instead of a blank card so the trader knows WHY (sample too small)
    // and what'd unblock signal (N more trades). Suppressed when there's
    // truly no data, or when the sample is already at the floor (those
    // are different problems handled elsewhere).
    const hint = buildEmptyScopeHint({ scope: 'setup', scopeId: setupName, tradeCount: enrichedSetupTrades.length });
    return hint ? [hint] : [];
  }
  // Profile-aware enrichment — annotate each observation with temporal
  // state (new, persistent, returning) drawn from the persistent Coach
  // Profile. Reads from the runtime singleton so we don't have to thread
  // the profile through every coach.analyze call site. No-op when no
  // profile has been built yet.
  const enriched = enrichWithTemporalContext(all, getRuntimeContext());
  // Synthesis pass — fuses the (now temporally aware) observations into a
  // single top-of-feed narrative ("State of <setup>"). The synthesis
  // observation is prepended with priority 100 so topObservations surfaces
  // it first; every analyzer observation is still in the array below it
  // for drill-down.
  const withSynthesis = synthesizeAndPrepend(enriched, { scope: 'setup', scopeId: setupName });
  const ranked = topObservations(withSynthesis, top);
  return ranked;
};
